import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { isIOS, isAndroid } from 'react-device-detect';
import { motion, AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';

// Components
import LandingPage from './components/LandingPage';
import TripPlanPage from './components/TripPlanPage';
import ProfilePage from './components/ProfilePage';
import ChatRoomPage from './components/ChatRoomPage';
import ChatInvitationPage from './components/ChatInvitationPage';
import WaitlistPage from './components/WaitlistPage';
import AppBanner from './components/AppBanner';
import SEOHead from './components/SEOHead';

// Hooks
import { useAppDetection } from './hooks/useAppDetection';
import { useDeepLinkDetection } from './hooks/useDeepLinkDetection';

// Styles
import './App.css';

function AppContent() {
  const location = useLocation();
  const { isAppInstalled } = useAppDetection();
  const { deepLinkType, deepLinkData } = useDeepLinkDetection();
  const [showBanner, setShowBanner] = useState(false);

  // Rileva se è Safari su iOS
  const isSafari = () => {
    if (!isIOS) return false;
    const ua = navigator.userAgent.toLowerCase();
    // Safari contiene "safari" ma NON "chrome" o "crios" (Chrome iOS)
    return ua.indexOf('safari') !== -1 && 
           ua.indexOf('chrome') === -1 && 
           ua.indexOf('crios') === -1 &&
           ua.indexOf('fxios') === -1 && // Firefox iOS
           ua.indexOf('edgios') === -1;  // Edge iOS
  };

  const isNonSafariIOS = isIOS && !isSafari();

  useEffect(() => {
    // Mostra banner personalizzato se:
    // 1. NON è iOS (Android/Desktop)
    // 2. È iOS ma NON è Safari (Chrome iOS, Firefox iOS, ecc.)
    const shouldShowBanner = (!isIOS || isNonSafariIOS) && 
                             !localStorage.getItem('wanderbuddy-banner-hidden');
    
    if (shouldShowBanner) {
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [isNonSafariIOS]);

  const hideBanner = () => {
    setShowBanner(false);
    localStorage.setItem('wanderbuddy-banner-hidden', 'true');
  };

  // Determina l'azione principale in base al dispositivo
  const getPrimaryAction = () => {
    // iOS: Usa Universal Links che aprono l'app se installata, altrimenti App Store
    if (isIOS) {
      // Se c'è un deep link specifico, usa l'Universal Link diretto
      if (deepLinkType) {
        return {
          text: '🔗 Apri nell\'App',
          // Universal Link: se app installata → apre app, altrimenti → App Store
          href: window.location.href,
          primary: true
        };
      }
      
      // Per la home page iOS, usa Universal Link alla welcome
      return {
        text: '📲 Visualizza su App Store',
        // Universal Link: se app installata → mostra popup "Apri in WanderBuddy"
        // se non installata → va all'App Store
        href: 'https://apps.apple.com/app/id6737308693',
        primary: true
      };
    }

    // Android/Desktop/Altri: vai alla waitlist
    return {
      text: '📧 Iscriviti alla Waitlist',
      href: '/waitlist',
      primary: true
    };
  };

  const getSecondaryAction = () => {
    // Su iOS: nessuna azione secondaria, solo pulsante principale
    if (isIOS) {
      return null;
    }
    
    // Su Android/Desktop: mostra link App Store come secondario
    return { 
      text: 'Visualizza su App Store', 
      href: 'https://apps.apple.com/app/id6737308693',
      external: true
    };
  };

  return (
    <>
      <SEOHead />
      
      <AnimatePresence>
        {showBanner && (
          <AppBanner onClose={hideBanner} isNonSafariIOS={isNonSafariIOS} />
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        style={{ paddingTop: showBanner ? '80px' : '0' }}
      >
        <Routes>
          <Route 
            path="/" 
            element={
              <LandingPage 
                primaryAction={getPrimaryAction()}
                secondaryAction={getSecondaryAction()}
                deepLinkType={deepLinkType}
                deepLinkData={deepLinkData}
                isAppInstalled={isAppInstalled}
                deviceType={isIOS ? 'ios' : isAndroid ? 'android' : 'desktop'}
              />
            } 
          />
          <Route 
            path="/trip/:tripId" 
            element={
              <TripPlanPage 
                primaryAction={getPrimaryAction()}
                secondaryAction={getSecondaryAction()}
                isAppInstalled={isAppInstalled}
              />
            } 
          />
          <Route 
            path="/share/trip/:tripId" 
            element={
              <TripPlanPage 
                primaryAction={getPrimaryAction()}
                secondaryAction={getSecondaryAction()}
                isAppInstalled={isAppInstalled}
                isShared={true}
              />
            } 
          />
          <Route 
            path="/profile/:nickname" 
            element={
              <ProfilePage 
                primaryAction={getPrimaryAction()}
                secondaryAction={getSecondaryAction()}
                isAppInstalled={isAppInstalled}
              />
            } 
          />
          <Route 
            path="/chatroom/:roomId" 
            element={
              <ChatRoomPage 
                primaryAction={getPrimaryAction()}
                secondaryAction={getSecondaryAction()}
                isAppInstalled={isAppInstalled}
              />
            } 
          />
          <Route 
            path="/chatroom/invitation/:roomId" 
            element={
              <ChatInvitationPage 
                primaryAction={getPrimaryAction()}
                secondaryAction={getSecondaryAction()}
              />
            }
          />
          <Route 
            path="/waitlist" 
            element={<WaitlistPage />} 
          />
          {/* Catch-all route per Universal Links */}
          <Route 
            path="*" 
            element={
              <LandingPage 
                primaryAction={getPrimaryAction()}
                secondaryAction={getSecondaryAction()}
                deepLinkType="unknown"
                deepLinkData={{ path: location.pathname }}
                isAppInstalled={isAppInstalled}
                deviceType={isIOS ? 'ios' : isAndroid ? 'android' : 'desktop'}
              />
            } 
          />
        </Routes>
      </motion.div>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <div className="App">
          <AppContent />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
